import React, { useEffect, useMemo, useState, useContext } from "react";
import API from "../../api/api";
import Navbar from "../../components/Navbar";
import SearchableSelect from "../../components/common/SearchableSelect";
import { AuthContext } from "../../context/AuthContext";

const MedalistsAchievements = () => {
  const { role, user } = useContext(AuthContext);
  const roleLower = String(role || "").toLowerCase();

  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedDistrict, setSelectedDistrict] = useState("all");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [selectedMedal, setSelectedMedal] = useState("all");
  const [districtOptions, setDistrictOptions] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [achievementsLoading, setAchievementsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const getYearFromEvent = (ev) => {
    const dateStr = ev?.event_date || ev?.date || ev?.start_date;
    if (!dateStr) return null;
    const parts = String(dateStr).split("-");
    if (parts.length > 0 && parts[0].length === 4) {
      return parts[0];
    }
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : String(parsed.getFullYear());
  };

  const loadEvents = async () => {
    try {
      const res = await API.fetchAdminEvents({ include_higher: true });
      const list = Array.isArray(res?.data)
        ? res.data
        : (Array.isArray(res) ? res : []);
      setEvents(list);
    } catch (err) {
      console.error("Failed to load events:", err);
    }
  };

  const loadDistricts = async () => {
    if (roleLower !== "district") {
      try {
        const res = await API.fetchDistrictOptions();
        setDistrictOptions(Array.isArray(res) ? res : []);
      } catch (err) {
        console.error("Failed to load district options:", err);
      }
    }
  };

  useEffect(() => {
    loadEvents();
    loadDistricts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;
    const loadAchievements = async () => {
      setAchievementsLoading(true);
      try {
        const res = await API.fetchAchievements({
          event_id: selectedEvent,
          year: selectedYear,
          district_id: selectedDistrict,
          level: selectedLevel,
          medal: selectedMedal
        });
        if (!alive) return;
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setAchievements(list);
      } catch (err) {
        console.error("Error loading achievements:", err);
      } finally {
        if (alive) setAchievementsLoading(false);
      }
    };
    loadAchievements();
    return () => { alive = false; };
  }, [selectedEvent, selectedYear, selectedDistrict, selectedLevel, selectedMedal]);

  const achievementsEventOptions = useMemo(() => {
    let list = Array.isArray(events) ? events : [];
    if (selectedYear !== "all") {
      list = list.filter((e) => {
        const evYear = getYearFromEvent(e);
        return String(evYear) === String(selectedYear);
      });
    }
    if (selectedLevel !== "all") {
      list = list.filter((e) => {
        const evLvl = String(e.event_level || "district").toLowerCase();
        return evLvl === String(selectedLevel).toLowerCase();
      });
    }
    return list;
  }, [events, selectedYear, selectedLevel]);

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Medalists & Participants Achievements</title>
          <style>
            body { font-family: sans-serif; padding: 20px; color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; font-size: 12px; }
            th { background-color: #f8fafc; font-weight: bold; }
            h1 { font-size: 20px; margin-bottom: 5px; color: #1e3a8a; }
            p { font-size: 12px; color: #64748b; margin-bottom: 20px; }
            .badge { display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: bold; font-size: 10px; text-transform: uppercase; }
            .badge-gold { background-color: #fef3c7; color: #b45309; border: 1px solid #fde68a; }
            .badge-silver { background-color: #f1f5f9; color: #334155; border: 1px solid #cbd5e1; }
            .badge-bronze { background-color: #ffedd5; color: #c2410c; border: 1px solid #fed7aa; }
            .badge-part { background-color: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
          </style>
        </head>
        <body>
          <h1>Medalists & Participants Achievements</h1>
          <p>Generated on ${new Date().toLocaleDateString()} | Active Filters: Year (${selectedYear}), Level (${selectedLevel}), Event (${selectedEvent}), Result/Medal (${selectedMedal})</p>
          <table>
            <thead>
              <tr>
                <th>Player Name</th>
                <th>Father Name</th>
                <th>Age</th>
                <th>District</th>
                <th>Event Name</th>
                <th>Game Type</th>
                <th>Result / Medal</th>
              </tr>
            </thead>
            <tbody>
              ${achievements.map(a => {
                let badgeClass = 'badge badge-part';
                if (a.medal === 'Gold') badgeClass = 'badge badge-gold';
                else if (a.medal === 'Silver') badgeClass = 'badge badge-silver';
                else if (a.medal === 'Bronze') badgeClass = 'badge badge-bronze';

                return `
                  <tr>
                    <td><strong>${a.player_name}</strong></td>
                    <td>${a.father_name}</td>
                    <td>${a.age} yrs</td>
                    <td>${a.district}</td>
                    <td>${a.event_title}</td>
                    <td>${a.game_name}</td>
                    <td><span class="${badgeClass}">${a.medal}</span></td>
                  </tr>
                `;
              }).join("")}
            </tbody>
          </table>
          <script>
            window.onload = function() { window.print(); window.close(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleExportCSV = () => {
    const headers = ["Player Name", "Father Name", "Age", "District", "Event Name", "Game Type", "Result/Medal"];
    const rows = achievements.map(a => [
      a.player_name,
      a.father_name,
      `${a.age} yrs`,
      a.district,
      a.event_title,
      a.game_name,
      a.medal
    ]);

    const csvContent = "\uFEFF" + [headers.join(","), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `achievements_${selectedYear}_level_${selectedLevel}_result_${selectedMedal}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!["admin", "secretary", "district"].includes(roleLower)) {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        Unauthorized Access
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto p-4 sm:p-6 mobile-page-pad">
        <section className="mb-6 rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-5 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-700">Analytics Board</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">Medalists & Participants Achievements</h1>
              <p className="text-sm text-slate-600 mt-1">
                {roleLower === "district"
                  ? `Showing achievements for players of ${user?.district || "your district"}`
                  : "Showing achievements across all districts"}
              </p>
            </div>
            {/* Export & Print actions */}
            <div className="flex gap-2 shrink-0">
              <button
                onClick={handleExportCSV}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all"
              >
                📥 Export Excel (CSV)
              </button>
              <button
                onClick={handlePrint}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all"
              >
                🖨️ Print / Save PDF
              </button>
            </div>
          </div>
        </section>

        {/* Filter Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-4 items-end mb-4">
            {/* Year Selector */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Filter by Year</label>
              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setSelectedEvent("all");
                }}
                className="w-full border border-slate-300 rounded-xl p-2 bg-white text-xs h-10"
              >
                <option value="all">All Years</option>
                <option value="2026">2026</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
              </select>
            </div>

            {/* Level Selector */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Filter by Level</label>
              <select
                value={selectedLevel}
                onChange={(e) => {
                  setSelectedLevel(e.target.value);
                  setSelectedEvent("all");
                }}
                className="w-full border border-slate-300 rounded-xl p-2 bg-white text-xs h-10"
              >
                <option value="all">All Levels</option>
                <option value="district">District</option>
                <option value="state">State</option>
                <option value="national">National</option>
                <option value="international">International</option>
              </select>
            </div>

            {/* District Selector (Visible for State Logins) */}
            {roleLower !== "district" ? (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Filter by District</label>
                <select
                  value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl p-2 bg-white text-xs h-10"
                >
                  <option value="all">All Districts</option>
                  {districtOptions.map((dist) => (
                    <option key={dist.id} value={dist.id}>
                      {dist.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="hidden md:block"></div>
            )}

            {/* Event Selector */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Filter by Event</label>
              <SearchableSelect
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
                className="w-full border border-slate-300 rounded-xl p-2 bg-white text-xs h-10"
              >
                <option value="all">All Events</option>
                {achievementsEventOptions.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title} {ev.event_level ? `(${String(ev.event_level).toUpperCase()})` : ""}
                  </option>
                ))}
              </SearchableSelect>
            </div>

            {/* Medal/Participation Selector */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Filter by Result</label>
              <select
                value={selectedMedal}
                onChange={(e) => setSelectedMedal(e.target.value)}
                className="w-full border border-slate-300 rounded-xl p-2 bg-white text-xs h-10"
              >
                <option value="all">All Results</option>
                <option value="gold">Gold Medal</option>
                <option value="silver">Silver Medal</option>
                <option value="bronze">Bronze Medal</option>
                <option value="medalists">All Medalists</option>
                <option value="participants">Participants Only</option>
              </select>
            </div>

            {/* Search Input */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Search Player</label>
              <input
                type="text"
                placeholder="Search by player name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border border-slate-300 rounded-xl p-2 bg-white text-xs h-10 focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
          </div>

          {achievementsLoading ? (
            <div className="flex items-center justify-center py-20">
              <span className="animate-spin inline-block w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full mr-3"></span>
              <span className="text-sm text-slate-600 font-semibold">Loading achievements data...</span>
            </div>
          ) : (
            <div className="overflow-x-auto border border-slate-100 rounded-xl mt-4">
              <table className="min-w-full text-sm text-left text-slate-700">
                <thead className="bg-slate-50 text-slate-700 font-semibold sticky top-0 border-b">
                  <tr>
                    <th className="p-3">Player Name</th>
                    <th className="p-3">Father Name</th>
                    <th className="p-3">Age</th>
                    <th className="p-3">District</th>
                    <th className="p-3">Event Name</th>
                    <th className="p-3">Game Type</th>
                    <th className="p-3 text-right">Result / Medal</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filtered = achievements.filter((a) => {
                      if (!searchQuery) return true;
                      const q = searchQuery.toLowerCase();
                      return (
                        String(a.player_name).toLowerCase().includes(q) ||
                        String(a.father_name).toLowerCase().includes(q)
                      );
                    });

                    if (filtered.length === 0) {
                      return (
                        <tr>
                          <td colSpan={7} className="text-center py-12 text-slate-400 font-medium">
                            No matching achievements or participation medals found.
                          </td>
                        </tr>
                      );
                    }

                    return filtered.map((ach, idx) => {
                      let badgeClass = "bg-slate-100 text-slate-700 border border-slate-200";
                      if (ach.medal === "Gold") {
                        badgeClass = "bg-amber-100 text-amber-700 border border-amber-200 font-bold";
                      } else if (ach.medal === "Silver") {
                        badgeClass = "bg-slate-200 text-slate-800 border border-slate-300 font-bold";
                      } else if (ach.medal === "Bronze") {
                        badgeClass = "bg-orange-100 text-orange-700 border border-orange-200 font-bold";
                      }

                      return (
                        <tr key={idx} className="border-b hover:bg-slate-50/50">
                          <td className="p-3 font-semibold text-slate-900">{ach.player_name}</td>
                          <td className="p-3">{ach.father_name}</td>
                          <td className="p-3">{ach.age} yrs</td>
                          <td className="p-3 font-medium text-slate-600">{ach.district}</td>
                          <td className="p-3 text-slate-600">{ach.event_title}</td>
                          <td className="p-3 text-slate-600">{ach.game_name}</td>
                          <td className="p-3 text-right">
                            <span className={`px-2.5 py-0.5 rounded text-[11px] uppercase tracking-wider font-semibold ${badgeClass}`}>
                              {ach.medal}
                            </span>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MedalistsAchievements;
